import React, { lazy, useCallback, useState } from 'react';
import { IdentificationModalWrapper } from '@fiverr-private/identification_core';
import { getContext } from '@fiverr-private/fiverr_context';
import { IdentificationFormProps } from '../../components/IdentificationForm/IdentificationForm.types';
import { StepId } from '../../constants/stepId';
import { AllocationGroups, Experiments } from '../../constants/experiments';

const LazyIdentificationForm = lazy(
    () => import(/* webpackChunkName: "LazyIdentificationBusinessForm" */ '../../components/IdentificationForm')
);

interface IdentificationModalProps {
    googleClientId: string;
    inLynxesGoogleOneTapFedCmRollout: boolean;
    inBeetlesMigrateSignupWebRollout: boolean;
    inBeetlesMigrateSigninWebRollout: boolean;
    inBeetlesMigrateSocialSignUpRollout: boolean;
    inCaribouSsoPromotionAllocationsRollout?: boolean;
    inCaribouSsoPromotionExperimentRollout?: boolean;
}

const CLOSABLE_STEPS: StepId[] = ['initial', 'email_identification', 'profile_setup'];

export const IdentificationModal: React.FC<IdentificationModalProps> = ({
    googleClientId,
    inLynxesGoogleOneTapFedCmRollout,
    inBeetlesMigrateSignupWebRollout,
    inBeetlesMigrateSigninWebRollout,
    inBeetlesMigrateSocialSignUpRollout,
    inCaribouSsoPromotionAllocationsRollout,
    inCaribouSsoPromotionExperimentRollout,
}) => {
    const { abTests } = getContext();
    const serverExperimentGroup = Number(abTests[Experiments.CARIBOU_SSO_PROMOTION_EXPERIMENT]) || undefined;
    // Track allocated group via callback (for client-side allocation)
    const [allocatedGroup, setAllocatedGroup] = useState<number | undefined>(serverExperimentGroup);
    const experimentGroup = allocatedGroup ?? serverExperimentGroup;
    const inGroupC = inCaribouSsoPromotionExperimentRollout && experimentGroup === AllocationGroups.C;

    const [closeOnOverlayClick, setCloseOnOverlayClick] = useState(true);

    const onStepIdChange: IdentificationFormProps['onStepIdChange'] = useCallback(
        (stepId) => {
            // Group C: disable overlay click only on code_activation step
            if (inGroupC && stepId === 'code_activation') {
                setCloseOnOverlayClick(false);

                return;
            }

            if (CLOSABLE_STEPS.includes(stepId)) {
                setCloseOnOverlayClick(true);
            } else {
                setCloseOnOverlayClick(false);
            }
        },
        [setCloseOnOverlayClick, inGroupC]
    );

    return (
        <IdentificationModalWrapper
            googleClientId={googleClientId}
            closeOnOverlayClick={closeOnOverlayClick}
            inLynxesGoogleOneTapFedCmRollout={inLynxesGoogleOneTapFedCmRollout}
            onSsoPromotionExperimentGroupAllocated={setAllocatedGroup}
            renderLazyComponent={({
                isModal,
                isModalOpen,
                closeModal,
                mode,
                redirectUrl,
                email,
                username,
                isSticky,
                blockedActionType,
                bigQueryEnrichment,
                mixpanelEnrichment,
                afterSigningSuccess,
                isSocialFlow,
                stepId,
                skipActivation,
                isCodeActivationSkippable,
                source,
                onSsoPromotionExperimentGroupAllocated,
            }) => (
                <LazyIdentificationForm
                    {...{
                        isModal,
                        isModalOpen,
                        closeModal,
                        mode,
                        redirectUrl,
                        email,
                        username,
                        isSticky,
                        blockedActionType,
                        bigQueryEnrichment,
                        mixpanelEnrichment,
                        afterSigningSuccess,
                        isSocialFlow,
                        stepId,
                        skipActivation,
                        isCodeActivationSkippable,
                        source,
                    }}
                    onStepIdChange={onStepIdChange}
                    inBeetlesMigrateSignupWebRollout={inBeetlesMigrateSignupWebRollout}
                    inBeetlesMigrateSigninWebRollout={inBeetlesMigrateSigninWebRollout}
                    inBeetlesMigrateSocialSignUpRollout={inBeetlesMigrateSocialSignUpRollout}
                    inCaribouSsoPromotionAllocationsRollout={inCaribouSsoPromotionAllocationsRollout}
                    inCaribouSsoPromotionExperimentRollout={inCaribouSsoPromotionExperimentRollout}
                    onSsoPromotionExperimentGroupAllocated={onSsoPromotionExperimentGroupAllocated}
                />
            )}
        />
    );
};
